export function getAcceptedOfferPriceFromOffer(offer) {
  if (!offer || offer.status !== "accepted") return null;
  // Purchased / redeemed offers no longer drive listing prices
  if (offer.redeemedAt) return null;
  const price = Number(
    offer.finalPrice ?? offer.counterPrice ?? offer.offeredPrice ?? 0
  );
  return price > 0 ? price : null;
}

export function getProductListingPrice(data) {
  return Number(data?.discountPrice ?? data?.discount_price ?? data?.price ?? 0);
}

export function getProductCardPriceDisplay(data, acceptedOfferPrice) {
  const listingPrice = getProductListingPrice(data);
  const original = Number(data?.originalPrice ?? data?.original_price ?? 0) || null;

  if (acceptedOfferPrice && acceptedOfferPrice > 0) {
    const strikePrice =
      listingPrice > acceptedOfferPrice
        ? listingPrice
        : original && original > acceptedOfferPrice
          ? original
          : null;

    return {
      displayPrice: acceptedOfferPrice,
      strikePrice,
      isAcceptedOffer: true,
    };
  }

  const showOriginal = original && original > listingPrice;
  return {
    displayPrice: listingPrice,
    strikePrice: showOriginal ? original : null,
    isAcceptedOffer: false,
  };
}

export function buildAcceptedOfferCartFields(listingPrice, acceptedUnitPrice) {
  const accepted = Number(acceptedUnitPrice || 0);
  const listing = Number(listingPrice ?? 0);
  if (!accepted || accepted <= 0) return null;
  return {
    discountPrice: accepted,
    isOfferPriceApplied: true,
    listingPriceAtOffer: listing,
  };
}

export function cartItemUsesAcceptedOfferPrice(cartItem, acceptedUnitPrice) {
  if (!cartItem || acceptedUnitPrice == null) return false;
  const expected = Number(acceptedUnitPrice);
  const actual = Number(cartItem.discountPrice || 0);
  if (!Number.isFinite(expected) || expected <= 0) return false;
  return Math.abs(actual - expected) < 0.011;
}

/**
 * Add a product to cart or increase qty when the same accepted-offer unit price applies.
 */
export function buildCartLineForAdd({
  product,
  stock,
  qty,
  listingPrice,
  acceptedUnitPrice,
  existingItem,
  extraFields = {},
}) {
  const quantityToAdd = Math.max(1, Number(qty || 1));
  const availableStock = Number(stock ?? product?.stock ?? 0);
  const listing = Number(listingPrice ?? getProductListingPrice(product) ?? 0);
  const accepted =
    acceptedUnitPrice != null && Number(acceptedUnitPrice) > 0
      ? Number(acceptedUnitPrice)
      : null;
  const offerFields = accepted ? buildAcceptedOfferCartFields(listing, accepted) : null;

  if (existingItem?.isBundleItemFromOffer || existingItem?.bundleOfferId) {
    return { error: "bundle_offer" };
  }

  let nextQty = quantityToAdd;
  if (existingItem) {
    if (offerFields) {
      if (!cartItemUsesAcceptedOfferPrice(existingItem, accepted)) {
        return { error: "different_price" };
      }
    } else {
      const existingPrice = Number(existingItem.discountPrice || 0);
      if (
        existingItem.isOfferPriceApplied ||
        Math.abs(existingPrice - listing) > 0.011
      ) {
        return { error: "different_price" };
      }
    }
    nextQty = Number(existingItem.qty || 1) + quantityToAdd;
  }

  if (availableStock > 0 && nextQty > availableStock) {
    return { error: "stock", stock: availableStock };
  }

  const base = {
    ...product,
    stock: availableStock,
    qty: nextQty,
    ...extraFields,
  };

  if (offerFields) {
    return { item: { ...base, ...offerFields }, merged: !!existingItem };
  }

  return {
    item: { ...base, discountPrice: listing },
    merged: !!existingItem,
  };
}

export function buildAcceptedOfferPriceMap(offers = []) {
  const map = {};

  for (const offer of offers) {
    if (offer?.status !== "accepted") continue;
    if (offer.redeemedAt) continue;
    if (offer.isBundleOffer) continue;

    const price = getAcceptedOfferPriceFromOffer(offer);
    if (!price) continue;

    const productId = String(offer.productId?._id || offer.productId || "");
    if (productId) {
      map[productId] = price;
    }
  }

  return map;
}
